/**
 * COPY STRATEGY — ExpenseNormPage
 *
 * Awareness:      Solution-Aware (Stage 3/5)
 *                 Reader knows two options exist (30% norm vs actual expenses),
 *                 needs help deciding which is better for them.
 *
 * Sophistication: Stage 3 — Mechanism
 *                 Shows HOW savings work via GPM/VSD/PSD tax breakdown table.
 *                 Not just "save money" but the specific mechanism of tax reduction.
 *
 * Avatar:         Mid-experience driver, €1500–2500/month revenue.
 *                 Already has individuali veikla, knows what 30% norm means.
 *                 Not a beginner (doesn't need IV explained) and not a high earner
 *                 (€30k+ section is a sidebar, not the focus).
 *
 * Hook:           Loss aversion — "galite prarasti šimtus eurų per metus"
 * Structure:      Educate → Compare → Calculate → CTA
 * Proof:          987€/year savings with concrete GPM/VSD/PSD grid
 * Funnel:         Related articles push to deeper content (60% strategy, fuel, taxes)
 */
import { motion } from 'framer-motion'
import { PiWarning, PiCheckCircle } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const ExpenseNormPage = () => {
  usePageSetup('30% norma ar faktinės išlaidos 2026: kas sutaupo daugiau?', {
    scroll: true,
    description: 'Pasirinkę netinkamą išlaidų metodą galite prarasti €987 per metus. Palyginimas su konkrečiais GPM, VSD ir PSD skaičiavimais — sužinokite, kuris metodas taupo daugiau.',
    type: 'article',
    datePublished: '2025-11-12',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText
          as="h1"
          className="text-zinc-900 text-2xl font-medium mt-3 mb-2"
        >
          30% norma ar faktinės išlaidos 2026: sutaupykite iki 2527€/metus
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Išlaidos</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">4 min skaitymo</span>
        </MotionDiv>

        <MotionText
          as="p"
          className="text-zinc-700 text-base mb-2"
        >
          Pasirinkus neteisingai, galite prarasti šimtus eurų per metus. Štai kaip nuspręsti.
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-base"
        >
          Turite du pasirinkimus: 30% išlaidų norma arba faktinės išlaidos. Šiame straipsnyje išsiaiškinsime, kuris variantas jums naudingesnis.
        </MotionText>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kas yra 30 % išlaidų norma?
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            Automatinė išlaidų apskaita
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-3">
            30 % norma reiškia, kad Mokesčių inspekcija automatiškai pripažįsta, jog 30 % jūsų pajamų sudaro išlaidos. Jums nereikia rinkti jokių kvitų ar įrodymų.
          </MotionText>

          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
            <MotionText as="p" className="text-zinc-700 text-sm font-medium mb-2">
              Pavyzdys:
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Pajamos: 2000€
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Išlaidos (30 %): 600 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono border-t border-zinc-250 pt-2 mt-2">
              Apmokestinamosios pajamos: 1400 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              GPM: 70 € (5% efektyvus tarifas)
            </MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs mt-1">
              5% tarifas gaunamas per mokesčio kreditą, jei metinis pelnas {'<'} 20k €
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h3"
          className="text-zinc-900 text-base font-medium mb-3"
        >
          30% normos privalumai:
        </MotionText>

        <MotionDiv className="space-y-3">
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Nereikia rinkti ir saugoti kvitų
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Paprasta apskaita – nereikia skaičiuoti
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Mažiau laiko skiriama dokumentų tvarkymui
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Nekyla problemų su VMI dėl kvitų
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kas yra faktinės išlaidos?
        </MotionText>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            Tikrosios išlaidos su įrodymais
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-3">
            Faktinės išlaidos – tai visos išlaidos, kurias galite įrodyti kvitais ar sąskaitomis faktūromis. Galite įtraukti daugiau nei 30 % – tiek, kiek iš tiesų išleidžiate.
          </MotionText>

          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
            <MotionText as="p" className="text-zinc-700 text-sm font-medium mb-2">
              Pavyzdys:
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Pajamos: 2000€
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono mb-1">
              Faktinės išlaidos:
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono ml-4">
              • Kuras: 400 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono ml-4">
              • Komisinis + PVM: 550 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono ml-4">
              • Draudimas: 50 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono ml-4">
              • Remontas: 100 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono border-t border-zinc-250 pt-2 mt-2">
              Iš viso išlaidų: 1100 € (55 %)
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Apmokestinamosios pajamos: 900 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono font-medium">
              GPM (5%): 45 €
            </MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs mt-1">
              (Jei metinės pajamos {'>'}20k €, tarifas didėtų)
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h3"
          className="text-zinc-900 text-base font-medium mb-3"
        >
          Faktinių išlaidų privalumai:
        </MotionText>

        <MotionDiv className="space-y-3 mb-4">
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Galite atskaityti daugiau nei 30 %
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Galite susigrąžinti iki 210 € per mėnesį
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Atspindi tikrąjį verslo vaizdą
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-3">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Didesnės išlaidos = mažesni mokesčiai
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                Faktinių išlaidų reikalavimai
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-amber-800 text-sm">
                  • Dokumente turi būti nurodytas jūsų vardas ir individualios veiklos numeris
                </li>
                <li className="text-amber-800 text-sm">
                  • Išlaidos turi būti susijusios su veikla
                </li>
                <li className="text-amber-800 text-sm">
                  • Dokumentus reikia saugoti 10 metų
                </li>
                <li className="text-amber-800 text-sm">
                  • Asmeniniai pirkiniai netinka
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <MotionDiv className="p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl mb-4">
          <MotionText as="h3" className="text-green-900 text-base font-medium mb-2">
            💰 Aukštai uždirbantiems pavežėjams
          </MotionText>
          <MotionText as="p" className="text-green-800 text-sm mb-3">
            Jei jūsų pajamos viršija €30 000/metus, galite išlikti 5% mokesčio zonoje naudojant 60% faktinių išlaidų strategiją.
          </MotionText>
          <MotionText as="p" className="text-green-700 text-sm mb-3">
            Tai reikalauja FR0457 formos ir verslo kortelės degalams, bet gali sutaupyti tūkstančius eurų per metus.
          </MotionText>
          <ButtonLink variant="primary" to="/blog/60-procentu-islaidos-strategija" className="inline-block">
            Skaityti pilną straipsnį →
          </ButtonLink>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kada rinktis 30% normą?
        </MotionText>

        <MotionDiv className="space-y-4">
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              ✅ Jūsų faktinės išlaidos mažesnės nei 30 % pajamų
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              ✅ Nenorite rinkti kvitų ir skaičiuoti
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              ✅ Vairuojate elektromobilį (mažos kuro išlaidos)
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-zinc-250 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm">
              ✅ Dirbate ne visą darbo dieną
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kada rinktis faktines išlaidas?
        </MotionText>

        <MotionDiv className="space-y-4 mb-4">
          <MotionDiv className="p-3 border border-green-200 bg-green-50 rounded-lg">
            <MotionText as="p" className="text-green-900 text-sm">
              ✅ Jūsų faktinės išlaidos viršija 30 % pajamų
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-green-200 bg-green-50 rounded-lg">
            <MotionText as="p" className="text-green-900 text-sm">
              ✅ Išleidžiate kurui €200+/mėn.
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-green-200 bg-green-50 rounded-lg">
            <MotionText as="p" className="text-green-900 text-sm">
              ✅ Mokate automobilį lizingu arba draudimą
            </MotionText>
          </MotionDiv>
          <MotionDiv className="p-3 border border-green-200 bg-green-50 rounded-lg">
            <MotionText as="p" className="text-green-900 text-sm">
              ✅ Reguliariai remontuojate ar servisuojate automobilį
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <MotionText as="h3" className="text-blue-900 text-sm font-medium mb-2">
            💡 Praktinis patarimas
          </MotionText>
          <MotionText as="p" className="text-blue-800 text-sm">
            Pirmaisiais metais rinkite visus kvitus ir skaičiuokite. Po metų pamatysite, ar jūsų išlaidos viršija 30 %. Tada galėsite nuspręsti, kuris metodas jums naudingesnis.
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kiek galite susigrąžinti?
        </MotionText>

        <MotionDiv className="mb-4">
          <MotionText as="p" className="text-zinc-700 text-sm mb-4">
            Jei jūsų faktinės išlaidos sudaro 50 % pajamų, o ne 30 %, susigrąžinsite:
          </MotionText>

          <MotionText as="p" className="text-zinc-500 text-xs mb-3">
            Pajamos: 24 000 €/metus (2000 €/mėn.)
          </MotionText>

          <MotionDiv className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm mb-3">
            <span className="text-zinc-500 text-xs font-medium">30 % norma</span>
            <span className="text-zinc-500 text-xs font-medium">50 % faktinės</span>

            <span className="text-zinc-700"><span className="text-zinc-400 text-xs">GPM</span> 840 €</span>
            <span className="text-zinc-700"><span className="text-zinc-400 text-xs">GPM</span> 484 €</span>

            <span className="text-zinc-700"><span className="text-zinc-400 text-xs">VSD</span> 1 893 €</span>
            <span className="text-zinc-700"><span className="text-zinc-400 text-xs">VSD</span> 1 352 €</span>

            <span className="text-zinc-700"><span className="text-zinc-400 text-xs">PSD</span> 1 056 €</span>
            <span className="text-zinc-700"><span className="text-zinc-400 text-xs">PSD</span> 966 €</span>

            <span className="text-zinc-900 font-medium border-t border-zinc-200 pt-1.5">3 789 €</span>
            <span className="text-zinc-900 font-medium border-t border-zinc-200 pt-1.5">2 802 €</span>
          </MotionDiv>

          <MotionDiv className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <MotionText as="p" className="text-green-800 text-sm font-medium">
              Sutaupoma: ~987 €/metus
            </MotionText>
            <MotionText as="p" className="text-green-700 text-xs mt-1">
              GPM −356 € · VSD −541 € · PSD −90 €
            </MotionText>
          </MotionDiv>

          <MotionText as="p" className="text-zinc-500 text-xs mt-3">
            (Jei metinis pelnas {'>'} 20k €, sutaupymas dar didesnis dėl aukštesnio tarifo)
          </MotionText>
        </MotionDiv>

        <FinhudasCTA awareness="problem" />

        <RelatedArticles articles={[
          { slug: '60-procentu-islaidos-strategija', title: '60% faktinės išlaidos: kaip uždirbti €45k ir mokėti tik 5% GPM' },
          { slug: 'kuro-apskaita-3-metodai-islaidos', title: 'Kuro išlaidos: 3 metodai ir dokumentų reikalavimai' },
          { slug: 'pavezejo-mokesciai-gpm-psd-vsd', title: 'Mokesčiai pavežėjui 2026' }
        ]} />

        <motion.div
          className="mt-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <ButtonLink variant="secondary" to="/blog">
            Grįžti į straipsnius
          </ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default ExpenseNormPage
